import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SavedLocation } from '@hero/schema';
import { SharedTable, type StatusBadgeConfigType } from '../../../app/Table/SharedTable';
import type { SearchInputProps } from '../../../app/Search/SearchInput';
import { SavedLocationRow } from './SavedLocationRow';
import { LocationIcon } from '@hero/web-icons';

export interface SavedLocationStatusFilterConfig {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: Array<{
    value: string;
    label: string;
    count?: number;
    icon?: React.ReactNode;
    className?: string;
  }>;
  counts?: Array<{ value: string; count: number }>;
}

export interface SavedLocationTableFilters {
  showSearch?: boolean;
  statusFilter?: SavedLocationStatusFilterConfig;
}

export interface SavedLocationTableProps {
  locations: SavedLocation[];
  isLoading?: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  searchProps?: SearchInputProps;
  onSort?: (column: string) => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  filters?: SavedLocationTableFilters;
  /** Empty-state shown only when the list is empty AND no filters/search are active. */
  emptyState?: React.ReactNode;
  /** Set to `true` whenever the user has search / filter / sort applied. */
  hasActiveFilters?: boolean;
  children?: React.ReactNode;
}

const LOCATION_STATUS_BADGE_CONFIG: StatusBadgeConfigType = {
  getStatusClass: (type: string) => {
    switch (type) {
      case 'own':
        return 'bg-primary-100 text-logoBlue';
      case 'client':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  },
  getStatusDisplayText: (status: string) => status,
  showIcon: true,
  getStatusIcon: () => <LocationIcon className="w-4 h-4 me-1" />,
};

const SavedLocationTableComponent: React.FC<SavedLocationTableProps> = ({
  locations,
  isLoading,
  sortColumn,
  sortDirection = 'asc',
  searchProps,
  onSort,
  onLoadMore = () => {},
  hasNextPage = false,
  isFetchingNextPage = false,
  filters,
  emptyState,
  hasActiveFilters,
  children,
}) => {
  const { t } = useTranslation(['m-location']);

  const columns = useMemo(
    () => [
      { key: 'name', label: t('m-location:table.columns.name', 'Name') },
      { key: 'location', label: t('m-location:table.columns.location', 'Location') },
      { key: 'client', label: t('m-location:table.columns.client', 'Client') },
      { key: 'contact', label: t('m-location:table.columns.contact', 'Contact') },
      { key: 'actions', label: '', sortable: false },
    ],
    [t],
  );

  return (
    <SharedTable
      data={locations}
      isLoading={isLoading}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      searchProps={searchProps}
      onSort={onSort}
      statusBadgeConfig={LOCATION_STATUS_BADGE_CONFIG}
      onLoadMore={onLoadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      emptyState={emptyState}
      hasActiveFilters={hasActiveFilters}
      getRowId={(location: SavedLocation) => location._id.toString()}>
      {children || <SavedLocationTableHead filters={filters} />}

      <SharedTable.Mid>
        <SharedTable.Rows
          columns={columns}
          rowRender={(location: SavedLocation) => <SavedLocationRow location={location} />}
        />
      </SharedTable.Mid>
    </SharedTable>
  );
};

const SavedLocationTableHead: React.FC<{ filters?: SavedLocationTableFilters }> = ({ filters }) => {
  if (!filters) return null;

  return (
    <SharedTable.Head>
      {filters.showSearch !== false && <SharedTable.Search />}
      {filters.statusFilter && (
        <SharedTable.Badge
          options={filters.statusFilter.options}
          counts={filters.statusFilter.counts}
          value={filters.statusFilter.value}
          onChange={filters.statusFilter.onChange}
          showCounts={!!filters.statusFilter.counts}
        />
      )}
    </SharedTable.Head>
  );
};

export const SavedLocationTable = Object.assign(SavedLocationTableComponent, {
  Head: SharedTable.Head,
  Search: SharedTable.Search,
  Badge: SharedTable.Badge,
  Mid: SharedTable.Mid,
  Rows: SharedTable.Rows,
});
